import type { ChannelDetail, ChannelInfo, IDevice, IBaseDevice } from '@core/devices/IDevice'
import { AvailableType, ChannelFixType, ChannelType, ConnectionState, DeviceType } from '@core/devices/IDevice'
import { Env } from '@core/EnvUtils'
import type { RemoteServiceResponse } from '@/netadapters/EndpointAdapter'
import { EndpointAdapter as NetAdapter } from '@/netadapters/EndpointAdapter'
import {
  ConnectTimeoutException,
  HttpAuthorizationException,
  HttpResourceNotFoundException,
  HttpServerException,
  RequestTimeoutException,
  InvalidParameterException,
  NoChannelAvailableException,
  UnrecoverableException,
} from '@core/Exceptions'
import { Base64 } from 'js-base64'
import { PATH_SEPARATOR } from '@core/Constants'
import { generateLanUrl, isEmpty, isRelativeUrl, transmissionTagTracker, URLBuilder } from '@core/Utils'
import { CloudAdapter } from '@/netadapters/CloudAdapter'
import type { IFileTransmissionProgressListener } from '@core/FileTransmissionProgressListener'
import { DefaultFileTransmissionProgressListener } from '@core/FileTransmissionProgressListener'
import type { HttpRequestCancelToken } from '@core/HttpRequestCancelToken'
import MD5 from 'crypto-js/md5'
import type { RequestConfig } from '@/netadapters/AbsNetAdapter'
import type { AxiosResponse } from 'axios'
import { getApiBaseOrigin, getAlbBaseOrigin } from '@/BuildConfig'
import { TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES, retry } from '@/core/decorators/RetryDecorator'
import { switchDomainName } from '@/BuildUtils'
import { gt } from 'lodash-es'

export interface Bean {
  model: string
  id: string
  type: DeviceType
  channel: { lan?: ChannelInfo | undefined | null; wan?: ChannelInfo | undefined | null }
  version: string
}

const DEFAULT_ID = ''

const API_FILE_DOWNLOAD = 'file/download'
const QUERY_PRAM_PATH = 'path'
const QUERY_PRAM_ACCESS_TOKEN = 'token'
const ALLOW_APPEND_TASK_VERSION = 15000000 + 1

class Device implements IDevice {
  #id: string = DEFAULT_ID
  #model = ''
  #type = DeviceType.PHONE
  #channels: Map<ChannelType, ChannelDetail> = new Map()
  #fixChannel: ChannelFixType = ChannelFixType.AutoSwitch // Temp for dev step for integrate & debug convenience
  #netAdapter
  #version = ''
  // TODO: 这里其实应该是可以用CloudAdapter里面的INSTANCE来直接用，但是在现在的测试框架下，INSTANCE里面的axios无法被mock，导致单元测试过不了，后续再解决
  #cloudAdapter = new CloudAdapter()
  isSupportZip?: boolean
  constructor(netAdapterNeedRetry = true) {
    this.#netAdapter = new NetAdapter(netAdapterNeedRetry)
  }

  get isDisabledTransmissionFile() {
    return gt(ALLOW_APPEND_TASK_VERSION, this.#version)
  }

  get version(): string {
    return this.#version
  }

  get model(): string {
    return this.#model
  }

  get connectionState(): ConnectionState {
    // 局域网的使用需要通过公网发送命令打开端口，所以如果公网不通，即设备直接是未连结，不用再检查局域网的通道情况
    const wan = this.#channels.get(ChannelType.WAN)

    switch (wan?.available) {
      case AvailableType.REACHABLE:
        return ConnectionState.CONNECTED
      case AvailableType.UNINITIALIZED:
        return ConnectionState.UNKNOWN
      default:
        return ConnectionState.UNCONNECTED
    }
  }

  get accessToken(): string | undefined {
    return this.#netAdapter.accessToken
  }

  static validateResult(result: unknown) {
    return result !== undefined
  }

  async updateAccessToken(updateToken: string | undefined = undefined) {
    if (DEFAULT_ID !== this.#id) {
      const token = updateToken ?? (await this.#cloudAdapter.getDeviceAccessToken(this.#id))

      if (token) {
        this.#netAdapter.setAccessToken(token)
      }
    }
  }

  canCallService() {
    return !!this.#getWanBaseUrl()
  }

  // TODO： 后续分场景加入重试
  async callService(
    id: string,
    data = {},
    config: RequestConfig = {},
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<AxiosResponse<RemoteServiceResponse> | undefined> {
    const baseRequestUrl = this.#getWanBaseUrl()

    if (baseRequestUrl) {
      return this.#netAdapter.callService(baseRequestUrl, id, data, config, requestCancelToken)
    }
    console.log(`[callService]: NoChannelAvailableException error ${new Date().toLocaleString()}`, id, data, config)

    throw new NoChannelAvailableException()
  }

  get type(): DeviceType {
    return this.#type
  }

  @retry(Device.validateResult, undefined, TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES)
  async uploadFile(
    targetPath: string,
    file: File,
    progressListener?: IFileTransmissionProgressListener,
    requestCancelToken?: HttpRequestCancelToken,
    fileMd5 = ''
  ): Promise<RemoteServiceResponse | undefined> {
    targetPath = Base64.encodeURI(targetPath)

    //由于设备端端口5分钟内未使用会被关闭，如果先获取地址，再计算MD5，大文件计算MD5会超过5分钟
    const baseRequestUrl = await this.#getLanBaseUrl()

    if (baseRequestUrl) {
      const listener = progressListener ? new DefaultFileTransmissionProgressListener(progressListener) : undefined

      if (requestCancelToken?.isCancelled) throw new UnrecoverableException()

      return this.#netAdapter.uploadFile(baseRequestUrl, file, fileMd5, targetPath, listener, requestCancelToken)
    }
    console.log(
      `[Transmission]: Lan uploadFile  error fileName: 
      ${file.name} ${new Date().toLocaleString()} NoChannelAvailableException`
    )
    throw new NoChannelAvailableException()
  }

  async getDownloadUrl(targetPath: string, forceRefreshToken = false, useLan = false): Promise<string | undefined> {
    if (forceRefreshToken) {
      await this.updateAccessToken()
    }

    const baseRequestUrl = await this.#generateDownLoadRequestUrl(useLan)

    if (!baseRequestUrl) throw new NoChannelAvailableException()

    if (!baseRequestUrl.pathname.endsWith(PATH_SEPARATOR)) {
      baseRequestUrl.pathname += PATH_SEPARATOR
    }

    const url = new URL(API_FILE_DOWNLOAD, baseRequestUrl.href)

    const targetPathBase64 = Base64.encodeURI(targetPath)

    url.searchParams.append(QUERY_PRAM_PATH, targetPathBase64)

    if (this.#netAdapter.accessToken) {
      url.searchParams.append(QUERY_PRAM_ACCESS_TOKEN, this.#netAdapter.accessToken)
    }

    return url.href
  }

  async #generateDownLoadRequestUrl(useLan: boolean): Promise<URL | undefined> {
    if (useLan) {
      const { lan } = this.#getChannel()
      const lanAvailable = await this.getLanAvailableInfo()

      if (lanAvailable === AvailableType.REACHABLE && lan?.host) {
        return this.#generateLanUrl(lan?.host)
      }
    } else {
      const baseUrl = await this.#getBaseRequestUrl(ChannelType.WAN)

      return baseUrl ? new URL(baseUrl) : baseUrl
    }
  }

  setModel(value: string): Device {
    this.#model = value

    return this
  }

  setId(id: string | undefined): Device {
    if (isEmpty(id)) {
      throw new InvalidParameterException('device id should be specified')
    }
    this.#id = id
    this.#netAdapter.setDeviceId(id)

    return this
  }

  setType(type: DeviceType): Device {
    this.#type = type

    return this
  }

  setVersion(version: string) {
    this.#version = version
  }

  get id(): string {
    return this.#id
  }

  // TODO: move such setter to builder to avoid expose to public
  setLanChannel(info: ChannelInfo | undefined | null) {
    this.#setChannelByType(ChannelType.LAN, info)

    return this
  }

  setWanChannel(info: ChannelInfo | undefined | null) {
    console.log('[setWanChannel]:', info)

    this.#setChannelByType(ChannelType.WAN, info)

    return this
  }

  fixChannel(channel: ChannelFixType) {
    if (Env.isRelease() || Env.isPreRelease()) return

    switch (channel) {
      case ChannelFixType.WanOnly:
      case ChannelFixType.LanOnly:
        this.#fixChannel = channel
        break
      default:
        this.#fixChannel = ChannelFixType.AutoSwitch
    }
  }

  /**
   * Get base URL
   *
   * @returns
   */
  async #getBaseRequestUrl(type?: ChannelType): Promise<URL | undefined> {
    const channel = type === undefined ? await this.#getAvailableChannel() : this.#channels.get(type)

    if (channel && channel.host) {
      const url = await this.#getBaseRequestUrlByChannelInfo(channel.type, channel.host)

      return url
    }

    return undefined
  }

  async #getBaseRequestUrlByChannelInfo(type: ChannelType, host: string): Promise<URL | undefined> {
    // TODO: set access token in header
    if (type === ChannelType.WAN) {
      return new URL(host) // FRP connection info only set the url in host
    }

    return this.#generateLanUrl(host)
  }

  async #generateLanUrl(host: string): Promise<URL | undefined> {
    const port = await this.#enableHttpsPort()

    if (!port) return undefined

    return generateLanUrl(host, port)
  }

  async #setChannelByType(type: ChannelType, info: ChannelInfo | undefined | null) {
    if (!info) {
      this.#channels.delete(type)

      return
    }

    const channel = this.#channels.get(type)

    if (type === ChannelType.WAN && isRelativeUrl(info.host)) {
      // wan如果是相对地址需要拼接前面的origin
      const url = new URLBuilder(getApiBaseOrigin())

      info.host = url.appendPath(info.host).toString()
    }

    if (!channel || info.host !== channel.host) {
      this.#setChannelAvailableByType(type, info, AvailableType.UNINITIALIZED)
    }
  }

  #setChannelAvailableByType(type: ChannelType, info: ChannelInfo | undefined, available: AvailableType) {
    if (!info) {
      this.#channels.delete(type)

      return
    }

    this.#channels.set(type, {
      host: info.host,
      available,
      type,
    })
  }

  async #getAvailableChannel(): Promise<ChannelDetail | undefined> {
    function isAvailable(channel: ChannelDetail | undefined) {
      return channel && channel.available === AvailableType.REACHABLE
    }

    const { lan, wan } = this.#getChannel()

    switch (this.#fixChannel) {
      case ChannelFixType.LanOnly:
        return lan
      case ChannelFixType.WanOnly:
        return wan
      case ChannelFixType.AutoSwitch:
      default:
        if (this.#channels.size > 0) {
          // Lan is high priority if available

          if (isAvailable(lan)) {
            return lan
          }

          if (isAvailable(wan)) {
            return wan
          }
        }
        break
    }

    return undefined
  }

  hashCode(): string {
    const currentDevice = JSON.stringify({
      id: this.id,
      model: this.model,
      channels: this.#getChannel(),
      type: this.type,
      version: this.version,
    })

    return MD5(currentDevice).toString()
  }

  equals(device: IDevice): boolean {
    return this.hashCode() === device.hashCode()
  }

  /**
   * @internal
   * @returns
   */
  getAllChannelInfo(): ChannelDetail[] {
    const channelInfo = []
    const { lan, wan } = this.#getChannel()

    if (lan) channelInfo.push(lan)
    if (wan) channelInfo.push(wan)

    return channelInfo
  }

  /**
   * @internal
   * @returns available has changed
   */
  updateChannelAvailability(detail: ChannelDetail): boolean {
    const channel = this.#channels.get(detail.type)

    const hasChanged = !!channel && channel.host === detail.host && channel.available !== detail.available

    if (hasChanged) channel.available = detail.available

    return hasChanged
  }

  /**
   * @internal
   */
  copyChannelAvailabilityWithSameHost(device: IDevice) {
    const { lan, wan } = this.#getChannel()

    const channelInfo = device.getAllChannelInfo()

    for (const channel of channelInfo) {
      if (channel.type === ChannelType.WAN) {
        if (channel.host === wan?.host) {
          wan.available = channel.available
        }
      } else if (channel.type === ChannelType.LAN) {
        if (channel.host === lan?.host) {
          lan.available = channel.available
        }
      }
    }
  }

  async getWanAvailableInfo(requestCancelToken?: HttpRequestCancelToken): Promise<AvailableType> {
    const { wan } = this.#getChannel()

    if (!wan?.host) {
      console.log(`[checkDeviceChannel] Wan unreachable:${new Date().toLocaleString()}`, wan)

      return AvailableType.UNREACHABLE
    }
    const tag = transmissionTagTracker.takeTag()

    try {
      const res = await this.#netAdapter.isDevicesChannelReachable(
        new URL(switchDomainName(wan.host, tag)),
        requestCancelToken
      )

      transmissionTagTracker.returnTag(tag)

      console.log(`[checkDeviceChannel] Wan:${new Date().toLocaleString()}`, res)

      wan.available = res ? AvailableType.REACHABLE : AvailableType.UNREACHABLE
    } catch (error) {
      transmissionTagTracker.returnTag(tag)

      console.log(`[checkDeviceChannel] Wan error:${new Date().toLocaleString()}`, error)

      wan.available = AvailableType.UNREACHABLE
    }

    return wan.available
  }

  //TODO: 去掉异步后需要通知文管使用同步方法
  async getLanAvailableInfo(
    forceRefresh?: boolean,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<AvailableType> {
    const { wan, lan } = this.#getChannel()

    if (!wan?.host || !lan?.host) {
      console.log(`[checkDeviceChannel] Lan unreachable:${new Date().toLocaleString()}`, wan, lan)

      return AvailableType.UNREACHABLE
    }

    if (forceRefresh) {
      const available = await this.#getHttpsAvailable(wan.host, lan.host, requestCancelToken)

      console.log(`[checkDeviceChannel] Lan available:${new Date().toLocaleString()}`, available)

      lan.available = available
    }

    return lan.available
  }

  async #getHttpsAvailable(
    wanHost: string,
    lanHost: string,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<AvailableType> {
    const httpsPort = await this.#netAdapter.enableHttpsPort(this.id, new URL(wanHost))

    if (!httpsPort) return AvailableType.UNREACHABLE

    const httpsUrl = generateLanUrl(lanHost, httpsPort)

    if (!httpsUrl) return AvailableType.UNREACHABLE

    try {
      const portRes = await this.#netAdapter.checkNetConnection(httpsUrl, requestCancelToken)

      console.log(`[checkDeviceChannel] Lan getHttpsAvailable:${new Date().toLocaleString()}`, portRes)

      if (!portRes) {
        return AvailableType.REACHABLE_ERROR_CERTIFICATION
      }
    } catch (error: unknown) {
      console.log(`[checkDeviceChannel] Lan getHttpsAvailable error:${new Date().toLocaleString()}`, error)

      if (this.#isLanInAvailable(error)) return AvailableType.UNREACHABLE

      return AvailableType.REACHABLE_ERROR_CERTIFICATION
    }

    return AvailableType.REACHABLE
  }

  async #getLanBaseUrl(): Promise<URL | undefined> {
    const { lan } = this.#getChannel()

    if (!lan) return undefined

    if (lan.available !== AvailableType.REACHABLE) return undefined

    const lanUrl = await this.#generateLanUrl(lan.host)

    return lanUrl
  }

  #getWanBaseUrl(): URL | undefined {
    const { wan } = this.#getChannel()

    if (!wan || !wan.host) return undefined

    return new URL(wan.host)
  }

  async #enableHttpsPort(): Promise<number | undefined> {
    const { wan } = this.#getChannel()

    if (!wan || !wan.host) return undefined

    const port = await this.#netAdapter.enableHttpsPort(this.id, new URL(wan.host))

    return port
  }

  #getChannel() {
    return { lan: this.#channels.get(ChannelType.LAN), wan: this.#channels.get(ChannelType.WAN) }
  }

  #isLanInAvailable(error: unknown): boolean {
    return (
      error instanceof RequestTimeoutException ||
      error instanceof HttpAuthorizationException ||
      error instanceof HttpResourceNotFoundException ||
      error instanceof HttpServerException ||
      error instanceof ConnectTimeoutException
    )
  }

  #changeUrlToALB(url: URL): string {
    return url.toString().replace(getApiBaseOrigin(), getAlbBaseOrigin())
  }
}

export const Builder = {
  build: (bean: Bean): IDevice => {
    const device = new Device()

    device
      .setModel(bean.model)
      .setId(bean.id)
      .setLanChannel(bean.channel.lan)
      .setWanChannel(bean.channel.wan)
      .setType(bean.type)
      .setVersion(bean.version)

    return device
  },

  buildFromDevice: (source: IDevice) => {
    const device = new Device()
    const channelInfo = source.getAllChannelInfo()
    const channelLan = {
      host: '',
    }
    const channelWan = {
      host: '',
    }

    for (const channel of channelInfo) {
      if (channel.type === ChannelType.WAN) channelWan.host = channel.host
      if (channel.type === ChannelType.LAN) channelLan.host = channel.host
    }

    device
      .setModel(source.model)
      .setId(source.id)
      .setLanChannel(channelLan)
      .setWanChannel(channelWan)
      .setVersion(source.version)

    return device
  },

  buildFromBasicInfo: (source: IBaseDevice) => {
    const device = new Device()

    const channelLan = source.channel?.find((item) => item.type === ChannelType.LAN)

    const channelWan = source.channel?.find((item) => item.type === ChannelType.WAN)

    device
      .setModel(source.model)
      .setId(source.id)
      .setLanChannel(channelLan)
      .setWanChannel(channelWan)
      .setVersion(source.version)

    device.updateAccessToken(source.accessToken)

    return device
  },
}
